import {
  AlertTriangle,
  Calendar,
  Check,
  Copy,
  Eye,
  EyeOff,
  Filter,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Router as RouterIcon,
  Search,
  Server,
  Shield,
  TerminalSquare,
  UserRound,
  Wifi,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp, fetchData } from "../../../services/apiService";

type Client = {
  id: string;
  name: string;
};

type RouterRow = {
  id: number | string;
  name: string;
  ip_address: string;
  vpn_type?: string;
  api_port?: number;
  client_id?: string;
  created_at?: string;
  client?: {
    id: string;
    name: string;
  };
  wg_private_key?: string | null;
};

type OnboardResponse = {
  router: RouterRow;
  mikrotik_script: string;
};

type FormData = {
  client_id: string;
  name: string;
  api_username: string;
  api_password: string;
};

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value?: number | null) {
  return new Intl.NumberFormat("en-KE").format(Number(value || 0));
}

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-KE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Africa/Nairobi",
  }).format(date);
}

function getInitials(name?: string) {
  if (!name) return "R";

  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return initials || "R";
}

function getClientName(router: RouterRow) {
  return router.client?.name || "Unassigned client";
}

function getVpnLabel(vpnType?: string | null) {
  if (!vpnType) return "L2TP";

  return vpnType.toUpperCase();
}

function StatCard({
  title,
  value,
  helper,
  icon,
  tone,
}: {
  title: string;
  value: number;
  helper: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div
        className={cn(
          "absolute right-[-34px] top-[-34px] h-24 w-24 rounded-full opacity-15 blur-2xl",
          tone,
        )}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <h3 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
            {formatNumber(value)}
          </h3>

          <p className="mt-1 text-xs font-medium text-slate-400">{helper}</p>
        </div>

        <div
          className={cn(
            "rounded-2xl p-3 text-white shadow-lg transition group-hover:scale-105",
            tone,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function MiniMetric({
  title,
  value,
  icon,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-white/15 p-2 text-white">{icon}</div>

        <div>
          <p className="text-xs font-medium text-white/60">{title}</p>
          <p className="text-xl font-black text-white">{formatNumber(value)}</p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  searchTerm,
  onCreate,
}: {
  searchTerm: string;
  onCreate: () => void;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-4 py-16 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
        <RouterIcon size={30} />
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">
        {searchTerm ? "No routers found" : "No routers yet"}
      </h3>

      <p className="mx-auto mt-1 max-w-md text-sm font-medium leading-6 text-slate-500">
        {searchTerm
          ? "Try searching with a different router name, IP address, or client name."
          : "Onboard your first MikroTik v6 router and generate the L2TP setup script."}
      </p>

      {!searchTerm && (
        <button
          onClick={onCreate}
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
        >
          <Plus size={17} />
          Add first router
        </button>
      )}
    </div>
  );
}

function CopyButton({
  value,
  copyKey,
  copiedKey,
  onCopy,
  label,
}: {
  value?: string | number | null;
  copyKey: string;
  copiedKey: string | null;
  onCopy: (text: string, key: string) => void;
  label?: string;
}) {
  if (!value) return null;

  return (
    <button
      type="button"
      onClick={() => onCopy(String(value), copyKey)}
      className="inline-flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-xs font-black text-slate-500 transition hover:bg-white hover:text-slate-900"
      title="Copy"
    >
      {copiedKey === copyKey ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      {label}
    </button>
  );
}

function RouterAvatar({ name }: { name: string }) {
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-sm">
      {getInitials(name)}
    </div>
  );
}

function RouterStatusBadge({ router }: { router: RouterRow }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">
      <Shield size={13} />
      {getVpnLabel(router.vpn_type)}
    </span>
  );
}

function DetailPill({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string | number | null;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{icon}</span>

        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
            {label}
          </p>

          <p className="truncate text-sm font-bold text-slate-800">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

function OnboardRouterModal({
  open,
  clients,
  form,
  errors,
  submitting,
  result,
  showPassword,
  copiedKey,
  onClose,
  onSubmit,
  onFormChange,
  onTogglePassword,
  onCopy,
}: {
  open: boolean;
  clients: Client[];
  form: FormData;
  errors: Record<string, string>;
  submitting: boolean;
  result: OnboardResponse | null;
  showPassword: boolean;
  copiedKey: string | null;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  onFormChange: (next: FormData) => void;
  onTogglePassword: () => void;
  onCopy: (text: string, key: string) => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white">
          <div className="absolute right-[-70px] top-[-70px] h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-3 text-slate-950 shadow-lg">
                <RouterIcon size={22} />
              </div>

              <div>
                <h2 className="text-xl font-black">Onboard L2TP Router</h2>

                <p className="mt-1 text-sm leading-5 text-white/60">
                  Create a router record and generate a MikroTik v6 L2TP setup
                  script.
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              type="button"
              disabled={submitting}
              className="rounded-2xl bg-white/10 p-2 text-white/70 transition hover:bg-white/15 hover:text-white disabled:opacity-50"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="max-h-[calc(92vh-100px)] overflow-y-auto px-5 py-5"
        >
          {errors.submit && (
            <div className="mb-4 flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {errors.submit}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Client <span className="text-rose-500">*</span>
              </label>

              <select
                value={form.client_id}
                onChange={(event) =>
                  onFormChange({ ...form, client_id: event.target.value })
                }
                className={cn(
                  "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                  errors.client_id
                    ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                    : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                )}
              >
                <option value="">Select client</option>

                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>

              {errors.client_id && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {errors.client_id}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Router Name <span className="text-rose-500">*</span>
              </label>

              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  onFormChange({ ...form, name: event.target.value })
                }
                className={cn(
                  "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                  errors.name
                    ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                    : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                )}
                placeholder="Example: Client Main Router"
              />

              {errors.name && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                API Username <span className="text-rose-500">*</span>
              </label>

              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={form.api_username}
                  onChange={(event) =>
                    onFormChange({
                      ...form,
                      api_username: event.target.value,
                    })
                  }
                  className={cn(
                    "w-full rounded-2xl border bg-slate-50 px-4 py-3 pl-10 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                    errors.api_username
                      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                  )}
                  placeholder="apiuser"
                />
              </div>

              {errors.api_username && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {errors.api_username}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                API Password <span className="text-rose-500">*</span>
              </label>

              <div className="relative">
                <Shield className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <input
                  type={showPassword ? "text" : "password"}
                  value={form.api_password}
                  onChange={(event) =>
                    onFormChange({
                      ...form,
                      api_password: event.target.value,
                    })
                  }
                  className={cn(
                    "w-full rounded-2xl border bg-slate-50 px-4 py-3 pl-10 pr-12 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                    errors.api_password
                      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                  )}
                  placeholder="••••••••"
                />

                <button
                  type="button"
                  onClick={onTogglePassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>

              {errors.api_password && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {errors.api_password}
                </p>
              )}
            </div>
          </div>

          {result && (
            <div className="mt-5 overflow-hidden rounded-3xl border border-emerald-100 bg-emerald-50">
              <div className="flex flex-col justify-between gap-3 border-b border-emerald-100 px-4 py-3 sm:flex-row sm:items-center">
                <div>
                  <h3 className="font-black text-emerald-900">
                    Router onboarded successfully
                  </h3>

                  <p className="mt-1 text-xs font-semibold text-emerald-700">
                    Copy this MikroTik script and run it on the client router.
                  </p>
                </div>

                <CopyButton
                  value={result.mikrotik_script}
                  copyKey="mikrotik-script"
                  copiedKey={copiedKey}
                  onCopy={onCopy}
                  label={
                    copiedKey === "mikrotik-script" ? "Copied" : "Copy script"
                  }
                />
              </div>

              <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap bg-slate-950 p-4 text-xs font-semibold leading-6 text-slate-100">
                {result.mikrotik_script}
              </pre>
            </div>
          )}

          <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              {result ? "Close" : "Cancel"}
            </button>

            {!result && (
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={17} className="animate-spin" />
                    Onboarding...
                  </>
                ) : (
                  <>
                    <Check size={17} />
                    Generate Router Script
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const RoutersPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [routers, setRouters] = useState<RouterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<string | "all">("all");
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState<FormData>({
    client_id: "",
    name: "",
    api_username: "apiuser",
    api_password: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OnboardResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showApiPassword, setShowApiPassword] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setPageError("");

    try {
      const clientsResponse = await fetchData(API_CONFIG.endpoints.clients);
      const safeClients = Array.isArray(clientsResponse) ? clientsResponse : [];

      setClients(safeClients);

      try {
        const routersResponse = await fetchData(`/routers-admin?vpn_type=l2tp`);
        setRouters(Array.isArray(routersResponse) ? routersResponse : []);
      } catch (error) {
        console.error("Routers load error:", error);
        setRouters([]);
      }
    } catch (error) {
      console.error("Load error:", error);
      setPageError("Failed to load routers. Please refresh and try again.");
      setClients([]);
      setRouters([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRouters = useMemo(() => {
    let list = routers;

    if (selectedClient !== "all") {
      list = list.filter((router) => router.client_id === selectedClient);
    }

    const query = searchTerm.trim().toLowerCase();

    if (query) {
      list = list.filter((router) => {
        return (
          router.name?.toLowerCase().includes(query) ||
          router.ip_address?.toLowerCase().includes(query) ||
          getClientName(router).toLowerCase().includes(query) ||
          String(router.api_port || "").includes(query)
        );
      });
    }

    return list;
  }, [routers, searchTerm, selectedClient]);

  const routersWithIp = useMemo(
    () => routers.filter((router) => Boolean(router.ip_address)).length,
    [routers],
  );

  const uniqueClientCount = useMemo(() => {
    const ids = new Set(
      routers.map((router) => router.client_id).filter(Boolean) as string[],
    );

    return ids.size;
  }, [routers]);

  const copyToClipboard = async (text: string, key: string) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);

      setTimeout(() => setCopiedKey(null), 1500);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const openCreate = () => {
    setShowModal(true);
    setResult(null);
    setErrors({});
    setShowApiPassword(false);

    setForm({
      client_id: clients[0]?.id ?? "",
      name: "",
      api_username: "apiuser",
      api_password: "",
    });
  };

  const closeModal = () => {
    if (submitting) return;

    setShowModal(false);
    setResult(null);
    setErrors({});
    setShowApiPassword(false);
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.client_id) {
      nextErrors.client_id = "Client is required.";
    }

    if (!form.name.trim()) {
      nextErrors.name = "Router name is required.";
    }

    if (!form.api_username.trim()) {
      nextErrors.api_username = "API username is required.";
    }

    if (!form.api_password.trim()) {
      nextErrors.api_password = "API password is required.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setErrors({});

    try {
      const payload = {
        client_id: form.client_id,
        name: form.name.trim(),
        api_username: form.api_username.trim(),
        api_password: form.api_password.trim(),
      };

      const response = (await fetchApp(`/routers-admin/onboard/l2tp-v6`, {
        method: "POST",
        body: JSON.stringify(payload),
      })) as OnboardResponse;

      setResult(response);

      if (response?.router) {
        setRouters((previous) => [response.router, ...previous]);
      }
    } catch (error) {
      console.error("Failed to onboard router:", error);

      setErrors({
        submit:
          "Failed to onboard router. Check API logs and confirm the backend response.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activeFilterLabel =
    selectedClient === "all"
      ? "All Clients"
      : clients.find((client) => client.id === selectedClient)?.name ||
        "Selected Client";

  return (
    <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
      <div className="w-full max-w-none space-y-4">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl md:p-6">
          <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute bottom-[-100px] left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-5 xl:flex-row xl:items-center">
            <div className="max-w-3xl">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-3xl bg-white p-4 text-slate-950 shadow-xl sm:block">
                  <RouterIcon size={28} />
                </div>

                <div>
                  <h1 className="text-3xl font-black tracking-tight md:text-4xl">
                    Routers • L2TP v6
                  </h1>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                    Onboard and manage MikroTik v6 routers, generate L2TP tunnel
                    scripts, and monitor router ownership by client.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[480px]">
              <MiniMetric
                title="Routers"
                value={routers.length}
                icon={<RouterIcon size={18} />}
              />

              <MiniMetric
                title="Clients"
                value={uniqueClientCount}
                icon={<UserRound size={18} />}
              />

              <MiniMetric
                title="With IP"
                value={routersWithIp}
                icon={<Wifi size={18} />}
              />
            </div>
          </div>

          <div className="relative mt-6 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-white/60">
              Generate the MikroTik setup script immediately after onboarding.
            </p>

            <button
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus size={17} />
              Add router
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Routers"
            value={routers.length}
            helper="All L2TP router records"
            icon={<RouterIcon size={23} />}
            tone="bg-blue-600"
          />

          <StatCard
            title="Linked Clients"
            value={uniqueClientCount}
            helper="Clients with routers"
            icon={<UserRound size={23} />}
            tone="bg-emerald-600"
          />

          <StatCard
            title="IP Ready"
            value={routersWithIp}
            helper="Routers with IP address"
            icon={<Wifi size={23} />}
            tone="bg-violet-600"
          />

          <StatCard
            title="Filtered"
            value={filteredRouters.length}
            helper={activeFilterLabel}
            icon={<Filter size={23} />}
            tone="bg-amber-500"
          />
        </section>

        <section className="rounded-3xl border border-white bg-white/95 p-4 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-slate-950 p-3 text-white">
                <Search size={18} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">Search & Filters</h2>

                <p className="mt-1 text-xs font-medium text-slate-400">
                  Search by router name, IP address, API port, or client name.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100 sm:w-[360px]">
                <Search size={17} className="shrink-0 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search routers..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full border-none bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                />

                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                    type="button"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              <div className="relative sm:w-[260px]">
                <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <select
                  value={selectedClient}
                  onChange={(event) =>
                    setSelectedClient(
                      event.target.value === "all" ? "all" : event.target.value,
                    )
                  }
                  className="w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 pl-10 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                >
                  <option value="all">All Clients</option>

                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={loadAll}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={17}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <button
                onClick={openCreate}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
              >
                <Plus size={17} />
                Add Router
              </button>
            </div>
          </div>

          {pageError && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <AlertTriangle size={17} className="mt-0.5 shrink-0" />
              {pageError}
            </div>
          )}
        </section>

        {loading ? (
          <div className="flex items-center justify-center gap-3 rounded-3xl border border-white bg-white/95 px-4 py-16 text-sm font-black text-slate-500 shadow-sm">
            <Loader2 size={22} className="animate-spin text-blue-600" />
            Loading routers...
          </div>
        ) : filteredRouters.length === 0 ? (
          <EmptyState searchTerm={searchTerm} onCreate={openCreate} />
        ) : (
          <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
            <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                  <Server size={20} />
                </div>

                <div>
                  <h2 className="font-black text-slate-950">
                    Router Inventory
                  </h2>

                  <p className="text-xs font-medium text-slate-400">
                    Showing {formatNumber(filteredRouters.length)} of{" "}
                    {formatNumber(routers.length)} routers
                  </p>
                </div>
              </div>

              {searchTerm && (
                <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
                  Search:{" "}
                  <span className="font-black text-slate-800">
                    “{searchTerm}”
                  </span>
                </div>
              )}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[1080px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-black uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-4">Router</th>
                    <th className="px-5 py-4">Client</th>
                    <th className="px-5 py-4">IP Address</th>
                    <th className="px-5 py-4">VPN</th>
                    <th className="px-5 py-4">API Port</th>
                    <th className="px-5 py-4">Created</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRouters.map((router) => (
                    <tr
                      key={router.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/80"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <RouterAvatar name={router.name} />

                          <div className="min-w-0">
                            <p className="max-w-[260px] truncate text-sm font-black text-slate-950">
                              {router.name}
                            </p>

                            <p className="mt-1 max-w-[260px] truncate font-mono text-xs font-semibold text-slate-400">
                              {router.id}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                          <UserRound size={15} className="text-slate-400" />
                          <span className="max-w-[220px] truncate">
                            {getClientName(router)}
                          </span>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-700">
                          <Wifi size={13} />
                          <span className="font-mono">
                            {router.ip_address || "—"}
                          </span>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <RouterStatusBadge router={router} />
                      </td>

                      <td className="px-5 py-4">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-black text-slate-700">
                          {router.api_port || "—"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                          <Calendar size={15} className="text-slate-400" />
                          {formatDate(router.created_at)}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-100 bg-slate-50 p-1">
                          <CopyButton
                            value={router.ip_address}
                            copyKey={`ip-${router.id}`}
                            copiedKey={copiedKey}
                            onCopy={copyToClipboard}
                            label={
                              copiedKey === `ip-${router.id}` ? "Copied" : "IP"
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 p-4 lg:hidden">
              {filteredRouters.map((router) => (
                <div
                  key={router.id}
                  className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <RouterAvatar name={router.name} />

                      <div className="min-w-0">
                        <h3 className="truncate text-base font-black text-slate-950">
                          {router.name}
                        </h3>

                        <p className="truncate text-xs font-semibold text-slate-400">
                          {getClientName(router)}
                        </p>
                      </div>
                    </div>

                    <RouterStatusBadge router={router} />
                  </div>

                  <div className="mt-4 grid gap-3 rounded-2xl bg-slate-50 p-3">
                    <DetailPill
                      label="IP Address"
                      value={router.ip_address}
                      icon={<Wifi size={15} />}
                    />

                    <DetailPill
                      label="API Port"
                      value={router.api_port}
                      icon={<TerminalSquare size={15} />}
                    />

                    <DetailPill
                      label="Created"
                      value={formatDate(router.created_at)}
                      icon={<Calendar size={15} />}
                    />

                    <DetailPill
                      label="Router ID"
                      value={router.id}
                      icon={<Server size={15} />}
                    />
                  </div>

                  <div className="mt-3 flex justify-end">
                    <CopyButton
                      value={router.ip_address}
                      copyKey={`mobile-ip-${router.id}`}
                      copiedKey={copiedKey}
                      onCopy={copyToClipboard}
                      label={
                        copiedKey === `mobile-ip-${router.id}`
                          ? "Copied"
                          : "Copy IP"
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <OnboardRouterModal
        open={showModal}
        clients={clients}
        form={form}
        errors={errors}
        submitting={submitting}
        result={result}
        showPassword={showApiPassword}
        copiedKey={copiedKey}
        onClose={closeModal}
        onSubmit={submit}
        onFormChange={setForm}
        onTogglePassword={() => setShowApiPassword((previous) => !previous)}
        onCopy={copyToClipboard}
      />
    </div>
  );
};

export default RoutersPage;
